<script setup>

import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { DateTime } from "luxon";

const { t } = useI18n();

// EDA dates are Austrian; render the Vienna calendar day. '—' for open/empty.
const fmtDate = (v) => {
  if (!v) return '—';
  const dt = DateTime.fromISO(String(v)).setZone('Europe/Vienna');
  return dt.isValid ? dt.toFormat('dd.MM.yyyy') : '—';
};

const props = defineProps({
  diffResult: { type: Object, required: true },
});

const mismatchesOnly = ref(true);
const searchText = ref('');

const statusColor = {
  'match': 'success',
  'mismatch': 'warning',
  'missing-in-mpm': 'error',
  'surplus-in-mpm': 'info',
};

const statusLabel = {
  'match': () => t('statusMatch'),
  'mismatch': () => t('statusMismatch'),
  'missing-in-mpm': () => t('statusMissingInMpm'),
  'surplus-in-mpm': () => t('statusSurplusInMpm'),
};

const summary = computed(() => props.diffResult.summary || {});

const displayRows = computed(() => {
  const rows = props.diffResult.rows || [];
  const search = searchText.value.trim().toLowerCase();
  return rows.filter(r => {
    if (mismatchesOnly.value && r.status === 'match') return false;
    if (!search) return true;
    const hay = [
      r.meteringPoint,
      r.senden?.consentId,
      r.mpm?.consentId,
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(search);
  });
});

// field name -> true if that field shows a diff for this row
function diffedFields(row) {
  const set = {};
  for (const d of row.fieldDiffs || []) set[d.field] = true;
  return set;
}

// highlight an attendance-factor period cell: explicit field diff, or a wholly missing/surplus period
function pHas(period, field, side) {
  if ((period.fieldDiffs || []).some(d => d.field === field)) return true;
  if (side === 'mpm' && period.status === 'surplus-in-mpm') return true;
  if (side === 'senden' && period.status === 'missing-in-mpm') return true;
  return false;
}

</script>
<script>
export default {
  name: 'PodlistDiffTable',
}
</script>


<template>
  <div>

    <div class="d-flex flex-wrap align-center ga-3 mb-3">
      <v-switch
        v-model="mismatchesOnly"
        :label="t('mismatchesOnly')"
        density="compact"
        hide-details
        color="primary"
      />
      <v-text-field
        v-model="searchText"
        :placeholder="t('podlistSearch')"
        density="compact"
        variant="outlined"
        max-width="320px"
        hide-details
        clearable
      />
    </div>

    <div class="d-flex flex-wrap ga-2 mb-4">
        <v-chip size="small" variant="flat">{{ t('summaryTotal') }}: {{ summary.total }}</v-chip>
        <v-chip size="small" color="success" variant="flat">{{ t('statusMatch') }}: {{ summary.match }}</v-chip>
        <v-chip size="small" color="warning" variant="flat">{{ t('statusMismatch') }}: {{ summary.mismatch }}</v-chip>
        <v-chip size="small" color="error" variant="flat">{{ t('statusMissingInMpm') }}: {{ summary.missingInMpm }}</v-chip>
        <v-chip size="small" color="info" variant="flat">{{ t('statusSurplusInMpm') }}: {{ summary.surplusInMpm }}</v-chip>
        <v-chip size="small" color="warning-2" variant="flat">{{ t('flagDateEndOffByOne') }}: {{ summary.offByOneDateEnd }}</v-chip>
        <v-chip size="small" color="badge-5" variant="flat">{{ t('flagMissingConsentId') }}: {{ summary.missingConsentId }}</v-chip>
        <v-chip size="small" color="warning-2" variant="flat">{{ t('afFactorDiffLabel') }}: {{ summary.attendanceFactorDiff }}</v-chip>
      </div>

    <div v-if="!displayRows.length" class="text-medium-emphasis my-4">{{ t('noRows') }}</div>

    <v-card
      v-for="row in displayRows"
      :key="row.key"
      variant="outlined"
      class="mb-3 pa-2"
    >
      <div class="d-flex align-center ga-2 mb-2">
        <v-chip size="small" :color="statusColor[row.status]" variant="flat">{{ statusLabel[row.status]() }}</v-chip>
        <span class="text-mono">{{ row.meteringPoint }}</span>
      </div>

      <v-row>
        <v-col cols="12" md="6">
          <v-card variant="outlined" class="pa-3 fill-height">
            <p class="text-subtitle-2 border-bottom mb-2 pb-1">{{ t('currentMpm') }}</p>
            <template v-if="row.mpm">
              <div :class="{ 'diff-cell': diffedFields(row)['dateStart'] }">{{ t('startDate') }}: {{ fmtDate(row.mpm.dateStart) }}</div>
              <div :class="{ 'diff-cell': diffedFields(row)['dateEnd'] }">{{ t('endDate') }}: {{ fmtDate(row.mpm.dateEnd) }}</div>
              <div :class="{ 'diff-cell': diffedFields(row)['consentId'] }">{{ t('consentId') }}: {{ row.mpm.consentId || '—' }}</div>
            </template>
            <div v-else class="text-medium-emphasis">{{ t('notInMpm') }}</div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card variant="outlined" class="pa-3 fill-height">
            <p class="text-subtitle-2 border-bottom mb-2 pb-1">{{ t('sendenEcp') }}</p>
            <template v-if="row.senden">
              <div :class="{ 'diff-cell': diffedFields(row)['dateStart'] }">{{ t('startDate') }}: {{ fmtDate(row.senden.dateActivate) }}</div>
              <div :class="{ 'diff-cell': diffedFields(row)['dateEnd'] }">{{ t('endDate') }}: {{ fmtDate(row.senden.dateDeactivate) }}</div>
              <div :class="{ 'diff-cell': diffedFields(row)['consentId'] }">{{ t('consentId') }}: {{ row.senden.consentId || '—' }}</div>
            </template>
            <div v-else class="text-medium-emphasis">{{ t('notSentByDso') }}</div>
          </v-card>
        </v-col>
      </v-row>

      <div v-if="row.attendanceFactor && row.attendanceFactor.periods.length" class="mt-3">
        <p class="text-subtitle-2 mb-1">{{ t('participationFactor') }}</p>
        <v-table density="compact" class="af-table">
          <thead>
            <tr>
              <th>{{ t('startDate') }}</th>
              <th>{{ t('endDate') }} ({{ t('afMpmShort') }})</th>
              <th>{{ t('endDate') }} ({{ t('afDsoShort') }})</th>
              <th>{{ t('participationFactor') }} ({{ t('afMpmShort') }})</th>
              <th>{{ t('participationFactor') }} ({{ t('afDsoShort') }})</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, i) in row.attendanceFactor.periods" :key="i">
              <td>{{ fmtDate(p.start) }}</td>
              <td :class="{ 'diff-cell': pHas(p, 'end', 'mpm') }">{{ p.mpm ? fmtDate(p.mpm.end) : '—' }}</td>
              <td :class="{ 'diff-cell': pHas(p, 'end', 'senden') }">{{ p.senden ? fmtDate(p.senden.end) : '—' }}</td>
              <td :class="{ 'diff-cell': pHas(p, 'factor', 'mpm') }">{{ p.mpm && p.mpm.factor != null ? p.mpm.factor : '—' }}</td>
              <td :class="{ 'diff-cell': pHas(p, 'factor', 'senden') }">{{ p.senden && p.senden.factor != null ? p.senden.factor : '—' }}</td>
              <td>
                <v-chip v-if="p.status !== 'match'" size="x-small" :color="statusColor[p.status]" variant="flat">
                  {{ statusLabel[p.status]() }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>
    </v-card>

  </div>
</template>

<style scoped>
.diff-cell {
  background-color: rgb(var(--v-theme-warning-light));
  font-weight: 600;
  border-radius: 4px;
  padding: 0 4px;
}
.text-mono {
  font-family: monospace;
}
</style>
